import { useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { AuthenticatedImage } from '@/components/features/authenticated-image'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

export type ImageLightboxItem = {
  src: string
  altCaption?: string
}

type ImageLightboxDialogProps = {
  src: string | null
  altCaption?: string
  onClose: () => void
  items?: ImageLightboxItem[]
  index?: number
  onIndexChange?: (index: number) => void
  testId?: string
  captionTestId?: string
  prevTestId?: string
  nextTestId?: string
}

/** Shared pan/zoom overlay used by document markdown and chat posted images. */
export function ImageLightboxDialog({
  src,
  altCaption = '',
  onClose,
  items,
  index = 0,
  onIndexChange,
  testId = testIds.markdownPreview.imageLightbox,
  captionTestId = testIds.markdownPreview.imageLightboxCaption,
  prevTestId,
  nextTestId,
}: ImageLightboxDialogProps) {
  const isOpen = !!src
  const gallery = Boolean(isOpen && items && items.length > 1 && onIndexChange)
  const count = items?.length ?? 0
  const current = gallery && items ? items[index] : null
  const resolvedSrc = current?.src ?? src
  const resolvedAlt = current?.altCaption ?? altCaption

  const openedAtRef = useRef(0)
  useEffect(() => {
    openedAtRef.current = isOpen ? Date.now() : 0
  }, [isOpen])

  const ignoreOpeningPointer = (e: { preventDefault: () => void }) => {
    if (Date.now() - openedAtRef.current < 350) e.preventDefault()
  }

  const go = (delta: number) => {
    if (!gallery || !items) return
    const next = (index + delta + items.length) % items.length
    onIndexChange(next)
  }

  useEffect(() => {
    if (!resolvedSrc) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        onClose()
        return
      }
      if (!gallery) return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        go(-1)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        go(1)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [resolvedSrc, gallery, index, items, onClose, onIndexChange])

  return (
    <Dialog open={!!resolvedSrc} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-slot="markdown-image-lightbox"
        showCloseButton
        onEscapeKeyDown={(e) => {
          e.preventDefault()
          onClose()
        }}
        onPointerDownOutside={ignoreOpeningPointer}
        onInteractOutside={ignoreOpeningPointer}
        className={cn(
          '!fixed !inset-4 !translate-x-0 !translate-y-0 !rounded-lg !p-4 !gap-3',
          'flex flex-col bg-background/95 shadow-lg backdrop-blur-sm',
          'h-[calc(100dvh-2rem)] w-auto max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] sm:!max-w-[calc(100vw-2rem)]',
        )}
        data-testid={testId}
      >
        <DialogTitle className="sr-only">Image preview</DialogTitle>
        <DialogDescription className="sr-only">
          Escape closes. Arrow keys move between images when more than one is open. Drag to pan when zoomed.
        </DialogDescription>
        <div className="relative flex min-h-0 flex-1 flex-col gap-3">
          {resolvedSrc && (
            <TransformWrapper
              key={resolvedSrc}
              centerOnInit
              initialScale={1}
              minScale={0.35}
              maxScale={14}
              limitToBounds={false}
              wheel={{ step: 0.12 }}
              pinch={{ disabled: false }}
              doubleClick={{ disabled: false, mode: 'reset', animationTime: 200 }}
            >
              <TransformComponent
                wrapperClass="!w-full grow min-h-[50vh]"
                contentClass="flex items-start justify-center"
              >
                <AuthenticatedImage
                  rawSrc={resolvedSrc}
                  alt={resolvedAlt || 'Preview'}
                  className="mx-auto block max-h-[calc(100dvh-11rem)] w-auto max-w-full select-none object-contain rounded-sm"
                />
              </TransformComponent>
            </TransformWrapper>
          )}
          {gallery ? (
            <>
              <button
                type="button"
                data-testid={prevTestId}
                aria-label="Previous image"
                className="absolute left-1 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-sm hover:bg-muted"
                onClick={(e) => {
                  e.stopPropagation()
                  go(-1)
                }}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                data-testid={nextTestId}
                aria-label="Next image"
                className="absolute right-1 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-sm hover:bg-muted"
                onClick={(e) => {
                  e.stopPropagation()
                  go(1)
                }}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          ) : null}
          <p
            data-testid={captionTestId}
            className="text-muted-foreground shrink-0 text-center text-[11px] leading-snug sm:text-xs"
          >
            {gallery
              ? `Esc closes · arrows next/prev · ${index + 1} of ${count}`
              : 'Esc closes · drag to pan · scroll to zoom · double-click resets'}
          </p>
          {resolvedAlt ? (
            <p className="text-muted-foreground line-clamp-2 shrink-0 text-center text-xs italic">
              {resolvedAlt}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}
